import React, { useEffect, useRef, useState } from "react";
import { Input, InputGroup, toaster, Toggle } from "rsuite";
import moment, { Moment } from "moment";
import { formatTimezoneOffset } from "../../../../../shared/Time.utils";
import { BksApi } from "../../../../../services/Bks.api";
import { PrimaryClockApi } from "../../../../../services/PrimaryClock.api";
import UpdateButton from "../../../../shared/UpdateButton/UpdateButton";
import NotificationFactory from "../../../../../shared/Notifications";
import DateTimeEditModal from "../../../../modals/DateTimeEditModal/DateTimeEditModal";
import {TfiSaveIcon, VscEditIcon} from "../../../../../models/Icons.model";
import {EspTimeProps} from "./EspTime.model";

const EspTime = ({ deviceId }: EspTimeProps) => {
  const [loading, setLoading] = useState(false);
  const [deviceTime, setDeviceTime] = useState<Moment | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>("+3");
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [dawnTime, setDawnTime] = useState<string | null>(null);
  const [duskTime, setDuskTime] = useState<string | null>(null);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [sunsetCorrection, setSunsetCorrection] = useState("");
  const [sunraiseCorrection, setSunraiseCorrection] = useState("");
  const [chipTemperature, setChipTemperature] = useState<number | null>(null);
  const [ledEnabled, setLedEnabled] = useState(false);
  const [systemDataLoading, setSystemDataLoading] = useState(false);

  const timeMomentRef = useRef<Moment | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const formatTimeHHmm = (timeString?: string | null) => {
    if (!timeString) return null;
    return timeString.slice(0, 5);
  };

  const formatChipTemperature = (value?: number | null) => {
    if (value == null || Number.isNaN(value)) return "";

    return `${value.toFixed(1)} °C`;
  };

  useEffect(() => {
    if (deviceId == null) return;
    getTime().then();
    getSystemData().then();
  }, [deviceId]);

  const getTime = async () => {
    setLoading(true);
    try {
      const response = await BksApi.getTime(deviceId);

      setDawnTime(formatTimeHHmm(response?.dawnTime));
      setDuskTime(formatTimeHHmm(response?.duskTime));

      if (response?.time) {
        const wallTime = response.time.split("+")[0];
        const timeMoment = moment(wallTime, "YYYY-MM-DDTHH:mm:ss");

        setDeviceTime(timeMoment);
        timeMomentRef.current = timeMoment.clone();
        setCurrentTime(timeMoment.format("DD.MM.YYYY HH:mm:ss"));

        const utcMatch = response.time.match(/([+-])(\d{2}):\d{2}$/);
        if (utcMatch) {
          const sign = utcMatch[1];
          const hours = utcMatch[2];
          setUserTimezone(sign + hours);
        }
      } else {
        setDeviceTime(null);
        timeMomentRef.current = null;
        setCurrentTime("");
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        if (timeMomentRef.current) {
          timeMomentRef.current.add(1, "seconds");
          setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
        }
      }, 1000);
    } catch (error) {
      console.error("Ошибка загрузки времени БКС", error);
      toaster.push(
        NotificationFactory.error("Ошибка получения времени БКС"),
        { placement: "topEnd" }
      );
      setDeviceTime(null);
      timeMomentRef.current = null;
      setCurrentTime("");
    } finally {
      setLoading(false);
    }
  };

  const getSystemData = async () => {
    try {
      const response = await PrimaryClockApi.getSystemData(deviceId);

      setSunsetCorrection(String(response?.sunSetCorrection ?? 0));
      setSunraiseCorrection(String(response?.sunRaiseCorrection ?? 0));
      setChipTemperature(response?.chipTemperature ?? null);
      setLedEnabled(Boolean(response?.ledEnabled));
    } catch (error) {
      console.error("Ошибка загрузки системных данных ESP", error);
      toaster.push(
        NotificationFactory.error("Ошибка получения системных данных ESP"),
        { placement: "topEnd" }
      );
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateUtc = () =>
      setCurrentUtcDateTime(moment().utc().format("DD.MM.YYYY HH:mm:ss"));

    updateUtc();
    const utcIntervalId = setInterval(updateUtc, 1000);

    return () => clearInterval(utcIntervalId);
  }, []);

  const handleTimezoneChange = (value: string) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const onSaveTimezone = async () => {
    if (!userTimezone || !/^[+-]\d{1,2}$/.test(userTimezone)) {
      toaster.push(
        NotificationFactory.error("Некорректный часовой пояс"),
        { placement: "topEnd" }
      );
      return;
    }

    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone);
      await BksApi.updateTimezone(deviceId, formattedTimezone);
      toaster.push(NotificationFactory.success(), {
        placement: "topEnd",
        duration: 3000,
      });
      await getTime();
    } catch (e) {
      console.error("Ошибка при сохранении часового пояса БКС:", e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateClockTime = async (
    localMoment: Moment,
    userTimezoneOffset: string
  ) => {
    setLoading(true);
    try {
      const hoursOffset = parseInt(userTimezoneOffset, 10);
      const safeHoursOffset = Number.isNaN(hoursOffset) ? 0 : hoursOffset;

      const year = localMoment.year();
      const month = localMoment.month();
      const day = localMoment.date();
      const hour = localMoment.hour();
      const minute = localMoment.minute();
      const second = localMoment.second();

      const utcMoment = moment
        .utc([year, month, day, hour, minute, second])
        .subtract(safeHoursOffset, "hours");

      const formattedTime = utcMoment.toISOString();

      const result = await BksApi.updateTime(deviceId, formattedTime);
      

      if (result) {
        toaster.push(
          NotificationFactory.success("Время успешно обновлено"),
          { placement: "topEnd" }
        );
        await getTime();
      } else {
        throw new Error("Не удалось обновить время");
      }
    } catch (e) {
      console.error("Ошибка при сохранении времени:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении времени"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const parseCorrection = (value: string, label: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      toaster.push(
        NotificationFactory.error(`Заполните поле: ${label}`),
        { placement: "topEnd" }
      );
      return null;
    }

    const correction = Number(trimmedValue);

    if (Number.isNaN(correction)) {
      toaster.push(
        NotificationFactory.error(`Некорректное значение поля: ${label}`),
        { placement: "topEnd" }
      );
      return null;
    }

    return correction;
  };

  const updateSunsetCorrection = async () => {
    const correction = parseCorrection(sunsetCorrection, "Коррекция заката");
    if (correction == null) return;

    setSystemDataLoading(true);
    try {
      await PrimaryClockApi.updateSunsetCorrection(deviceId, correction);
      toaster.push(NotificationFactory.success("Коррекция заката сохранена"), {
        placement: "topEnd",
      });
      await getSystemData();
    } catch (e) {
      console.error("Ошибка при сохранении коррекции заката:", e);
      toaster.push(NotificationFactory.error("Ошибка сохранения коррекции заката"), {
        placement: "topEnd",
      });
    } finally {
      setSystemDataLoading(false);
    }
  };

  const updateSunraiseCorrection = async () => {
    const correction = parseCorrection(sunraiseCorrection, "Коррекция восхода");
    if (correction == null) return;

    setSystemDataLoading(true);
    try {
      await PrimaryClockApi.updateSunraiseCorrection(deviceId, correction);
      toaster.push(NotificationFactory.success("Коррекция восхода сохранена"), {
        placement: "topEnd",
      });
      await getSystemData();
    } catch (e) {
      console.error("Ошибка при сохранении коррекции восхода:", e);
      toaster.push(NotificationFactory.error("Ошибка сохранения коррекции восхода"), {
        placement: "topEnd",
      });
    } finally {
      setSystemDataLoading(false);
    }
  };

  const handleLedToggle = async (enabled: boolean) => {
    setLedEnabled(enabled);
    setSystemDataLoading(true);

    try {
      await PrimaryClockApi.updateLedState(deviceId, enabled);
      toaster.push(NotificationFactory.success("Состояние LED обновлено"), {
        placement: "topEnd",
      });
    } catch (e) {
      console.error("Ошибка при обновлении состояния LED:", e);
      setLedEnabled((prev) => !prev);
      toaster.push(NotificationFactory.error("Ошибка обновления состояния LED"), {
        placement: "topEnd",
      });
    } finally {
      setSystemDataLoading(false);
    }
  };

  const handleRefresh = async () => {

    await Promise.all([getTime(), getSystemData()]);
  };

  return (
    <>
      <div className="fieldset-wrapper mb-4">
        <div className="legend-title main-legend-title">БКС</div>
        <section className="primary-clock p-2 pb-2">
          <div className="row">
            <div className="col-md-12 text-end">
              <UpdateButton loading={loading || systemDataLoading} onRefresh={handleRefresh} />
            </div>
          </div>

          <div className="row align-items-center pt-1">
            <div className="col-md-4">
              <InputGroup style={{ marginBottom: "5px" }}>
                <InputGroup.Addon style={{ width: 130 }}>
                  Время и дата
                </InputGroup.Addon>
                <Input
                  className="bg-green"
                  readOnly
                  value={currentTime || ""}
                />
                <InputGroup.Addon>
                  <VscEditIcon
                    onClick={() => setIsTimeModalOpen(true)}
                    style={{ cursor: "pointer" }}
                    title="Редактировать"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>

            <div className="col-md-3">
              <InputGroup style={{ width: 220 }} className="mb-1">
                <InputGroup.Addon>Час. пояс</InputGroup.Addon>
                <Input
                  value={userTimezone || ""}
                  onChange={handleTimezoneChange}
                  style={{ textAlign: "center" }}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{ cursor: "pointer" }}
                    onClick={onSaveTimezone}
                    title="Сохранить часовой пояс"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>

            <div
              className="col"
              style={{ padding: "0px 5px", marginBottom: "5px" }}
            >
              <InputGroup>
                <InputGroup.Addon>Восход:</InputGroup.Addon>
                <Input
                  className="bg-violet"
                  style={{ width: "100%" }}
                  readOnly
                  value={dawnTime || ""}
                />
              </InputGroup>
            </div>

            <div
              className="col"
              style={{ padding: "0px 5px", marginBottom: "5px" }}
            >
              <InputGroup>
                <InputGroup.Addon>Закат:</InputGroup.Addon>
                <Input
                  className="bg-violet"
                  style={{ width: "100%" }}
                  readOnly
                  value={duskTime || ""}
                />
              </InputGroup>
            </div>
          </div>
          <div className="row">
            <h6 className="mb-2">Коррекция</h6>
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Addon>Восход</InputGroup.Addon>
                <Input
                  style={{ width: "100%" }}
                  value={sunraiseCorrection}
                  onChange={setSunraiseCorrection}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{cursor: "pointer"}}
                    onClick={updateSunraiseCorrection}
                    title="Сохранить коррекцию восхода"
                  />
                </InputGroup.Addon>
              </InputGroup>
              <InputGroup className="mt-1">
                <InputGroup.Addon>Закат</InputGroup.Addon>
                <Input
                  style={{ width: "100%" }}
                  value={sunsetCorrection}
                  onChange={setSunsetCorrection}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                  style={{cursor: "pointer"}}
                  onClick={updateSunsetCorrection}
                  title="Сохранить коррекцию заката"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup className="mb-1">
                <InputGroup.Addon>Температура ESP</InputGroup.Addon>
                <Input
                  readOnly
                  value={formatChipTemperature(chipTemperature)}
                />
              </InputGroup>
              <Toggle
                className="mt-2"
                size="md"
                checked={ledEnabled}
                disabled={systemDataLoading}
                loading={systemDataLoading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                onChange={handleLedToggle}
              >
                LED
              </Toggle>
            </div>
          </div>
        </section>
      </div>

      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment: Moment) => {
          setDeviceTime(newMoment);
          timeMomentRef.current = newMoment.clone();
          setCurrentTime(newMoment.format("DD.MM.YYYY HH:mm:ss"));

          await updateClockTime(newMoment, userTimezone);
          setIsTimeModalOpen(false);
        }}
      />
    </>
  );
};

export default EspTime;
